import { create } from "zustand";
import externalApiKeyService, {
  CreateApiKeyPayload,
  ExternalApiKey,
  ExternalApiService,
} from "../service/externalApiKey.service";
import alertService from "../components/alert/alertService";
import { t } from "i18next";

type ExternalApiKeyState = {
  keys: ExternalApiKey[];
  services: ExternalApiService[];
  loading: boolean;
  initialized: boolean;
  openModal: boolean;
};

type ExternalApiKeyActions = {
  fetchKeys: () => Promise<void>;
  fetchServices: () => Promise<void>;
  createKey: (payload: CreateApiKeyPayload) => Promise<string | undefined>;
  revokeKey: (id: string) => Promise<void>;
  reset: () => void;
  setOpenModal: () => void;
  regenerateKey: (id: string) => Promise<string | undefined>;
  updateDescription: (id: string, description: string) => Promise<void>;
};

export const useExternalApiKeyStore = create<
  ExternalApiKeyState & ExternalApiKeyActions
>((set, get) => ({
  keys: [],
  services: [],
  loading: false,
  initialized: false,
  openModal: false,

  // 🔹 โหลดรายการคีย์
  fetchKeys: async () => {
    set({ loading: true });

    try {
      const keys = await externalApiKeyService.getKeys();

      set({
        keys: keys ?? [],
        initialized: true,
        loading: false,
      });
    } catch (error) {
      set({ loading: false });
    }
  },

  // 🔹 โหลดรายการ service (dropdown)
  fetchServices: async () => {
    set({ loading: true });

    try {
      const services = await externalApiKeyService.getServices();

      set({
        services: services ?? [],
        loading: false,
      });
    } catch (error) {
      set({ loading: false });
    }
  },

  // 🔹 สร้าง API key ใหม่
  createKey: async (payload: CreateApiKeyPayload) => {
    set({ loading: true });
    try {
      const newKey = await externalApiKeyService.createKey(payload);

      if (newKey) {
        set((state) => ({
          keys: [newKey, ...state.keys],
          loading: false,
        }));

        return newKey.apiKey;
      } else {
        set({ loading: false });
      }
    } catch (error) {
      set({ loading: false });
    }
  },

  // 🔹 ยกเลิก / revoke key
  revokeKey: async (id: string) => {
    set({ loading: true });
    try {
      const ok = await externalApiKeyService.revokeKey(id);

      if (ok) {
        set((state) => ({
          keys: state.keys.map((k) =>
            k.refId === id ? { ...k, status: "delete" } : k
          ),
          loading: false,
        }));

        alertService.mixin(t("Successfully"));
      } else {
        set({ loading: false });
      }
    } catch (error) {
      set({ loading: false });
    }
  },

  /** 🔹 Regenerate API key (จะได้ key ใหม่กลับมา ใช้โชว์ครั้งเดียว) */
  regenerateKey: async (id: string) => {
    set({ loading: true });
    try {
      const newKey = await externalApiKeyService.regenerateKey(id);

      if (newKey) {
        set((state) => ({
          keys: state.keys.map((k) => (k.refId === id ? newKey : k)),
          loading: false,
        }));

        return newKey.apiKey;
      } else {
        set({ loading: false });
      }
    } catch (error) {
      set({ loading: false });
    }
  },

  /** 🔹 Update description ของ API key ตาม refId */
  updateDescription: async (id: string, description: string) => {
    set({ loading: true });
    try {
      const newDesc = await externalApiKeyService.updateDescription(
        id,
        description
      );

      if (newDesc) {
        set((state) => ({
          keys: state.keys.map((k) => (k.refId === id ? newDesc : k)),
          loading: false,
        }));

        alertService.mixin(t("Successfully"));
      } else {
        set({ loading: false });
      }
    } catch (error) {
      set({ loading: false });
    }
  },

  // 🔹 clear state
  reset: () => {
    set({
      keys: [],
      services: [],
      loading: false,
      initialized: false,
    });
  },

  setOpenModal: () => {
    const { openModal } = get();
    set({ openModal: !openModal });
  },
}));
